import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, ActivatedRoute, Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { UserProfile, UserRole } from '../../../shared/models/auth.model';
import { LettersService } from '../../../shared/services/letters.service';
import {
  AuditsData,
  LetterDetails,
} from '../../../shared/models/letters.model';
import { DocumentAttachment } from '../../../shared/models/routing-slip.model';
import { AuditModalComponent } from '../../../shared/components/ui/audit-modal/audit-modal.component';
import { DocumentViewerModalComponent } from '../../../shared/components/ui/document-viewer-modal/document-viewer-modal.component';
import { AuthService } from '../../../core/services/auth.service';
import { DocumentService } from '../../../shared/services/document.service';
import { formatDateDDMMYYYY } from '../../../shared/utils/date.util';
import { ModalComponent } from '../../../shared/components/ui/modal/modal.component';
import { LoaderComponent } from '../../../shared/components/ui/loader/loader.component';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';

interface DocumentInfo {
  uploadDate: string;
  assignedDate: string;
  dueDate: string;
  source: string;
  documentName: string;
}

@Component({
  selector: 'app-letters-details',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    ButtonModule,
    AuditModalComponent,
    DocumentViewerModalComponent,
    ModalComponent,
    LoaderComponent,
    ToastModule,
  ],
  providers: [LettersService, DocumentService, MessageService],
  templateUrl: './letters-details.component.html',
  styleUrl: './letters-details.component.scss',
})
export class LettersDetailsComponent implements OnInit {
  // Sidebar state
  @Output() auditLog = new EventEmitter<any>();
  selectedRow: any;
  auditData: AuditsData[] = [];
  auditDialogVisible: boolean = false;

  // Document viewer modal state
  showDocumentViewer: boolean = false;
  currentDocument: DocumentAttachment | null = null;

  // Role-based access control
  isManager: boolean = false;
  showReviewButtons: boolean = false;

  // User profile data - will be set dynamically from authenticated user
  userProfile: UserProfile | null = null;

  // Document info
  documentName: string = '';
  documentId: string = '';

  // Document information
  documentInfo: DocumentInfo = {
    uploadDate: '',
    assignedDate: '',
    dueDate: '',
    source: '',
    documentName: '',
  };

  // Loading state for document info
  documentInfoLoading: boolean = false;

  // Loading state for assigned users
  assignedUsersLoading: boolean = false;

  // Full letter details
  letterDetails?: LetterDetails;

  // Assigned users from API
  assignedUsers: any[] = [];

  // Approve/Reject status from API
  approveRejectStatus: number = 3; // Default to 3 (pending)

  // Archive status from API
  isArchived: boolean = false;

  // Archive functionality
  showDeleteModal: boolean = false;
  isDeleting: boolean = false;

  isMobile = window.innerWidth < 640;
  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private lettersService: LettersService,
    private authService: AuthService,
    private documentService: DocumentService,
    private messageService: MessageService
  ) {}

  ngOnInit(): void {
    this.checkUserRole();
    this.loadDocumentInfo();
  }

  private checkUserRole(): void {
    const currentUser = this.authService.getCurrentUser();

    // Set userProfile from authenticated user
    if (currentUser) {
      this.userProfile = {
        id: currentUser.email,
        name: currentUser.name,
        email: currentUser.email,
        role: currentUser.role,
        avatar: 'assets/images/logo.png',
      };
    }

    this.isManager = this.authService.hasRole(UserRole.MANAGER);
  }
  onAuditLog(row: any) {
    this.selectedRow = row;

    // Load audit log data from API
    this.loadAuditLog();
    this.auditDialogVisible = true;
  }

  private loadDocumentInfo(): void {
    this.route.queryParams.subscribe((params) => {
      if (params['id']) {
        this.documentId = params['id'];

        // Check if document is archived from query params (when navigating from archive list)
        if (params['archived'] === 'true') {
          this.isArchived = true;
        }

        // Check if letter was just saved/updated and show success message
        if (params['saved'] === 'true') {
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Letter added successfully',
            life: 3000,
          });
          // Remove the query parameter from URL to avoid showing message on refresh
          setTimeout(() => {
            this.router.navigate(['/letters/details'], {
              queryParams: { id: this.documentId },
              replaceUrl: true,
            });
          }, 100);
        } else if (params['updated'] === 'true') {
          // Show success message for updated letter
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Letter updated successfully',
            life: 5000,
          });
          // Remove the query parameter from URL to avoid showing message on refresh
          // Delay URL replacement to allow message to be visible
          setTimeout(() => {
            this.router.navigate(['/letters/details'], {
              queryParams: { id: this.documentId },
              replaceUrl: true,
            });
          }, 2000);
        }

        this.loadLetterDetails();
      }
    });
  }

  private loadLetterDetails(): void {
    this.documentInfoLoading = true;
    this.assignedUsersLoading = true;

    this.lettersService.getLetterById(this.documentId).subscribe({
      next: (response) => {
        // Transform the API response to match our component needs
        this.documentName = response.documentName;

        // Get source name - handle both sourceId (number) and source (string) from API
        const sourceName =
          response.source || this.getSourceName(response.sourceId);

        // Format dates with fallback to "Not Assigned"
        const uploadDate = response.uploadDate
          ? formatDateDDMMYYYY(response.uploadDate)
          : 'Not Assigned';
        const assignedDate = response.uploadDate
          ? formatDateDDMMYYYY(response.uploadDate)
          : 'Not Assigned'; // Using uploadDate as assigned date
        const dueDate = response.dueDate
          ? formatDateDDMMYYYY(response.dueDate)
          : 'Not Assigned';

        this.documentInfo = {
          uploadDate: uploadDate,
          assignedDate: assignedDate,
          dueDate: dueDate,
          source: sourceName,
          documentName: response.documentName,
        };

        // Store assigned users array for UI display
        this.assignedUsers = response.assignedUsers || [];

        // Store approveRejectStatus for button visibility logic
        this.approveRejectStatus = response.approveRejectStatus || 3; // Default to 3 (pending)

        // Store archived status for button visibility logic
        // Check for various possible field names that indicate archived/deleted status
        // API response takes precedence over query param
        const apiIndicatesArchived = !!(
          (
            response.isDeleted ||
            response.isArchived ||
            response.deleted ||
            response.isDeletedFlag ||
            response.deletedOn ||
            response.deletedDate ||
            (response.status !== undefined && response.status === 4)
          ) // Sometimes archived status is a number
        );

        // Use API response if available, otherwise keep the query param value
        if (apiIndicatesArchived) {
          this.isArchived = true;
        }
        // If API doesn't indicate archived, keep the query param value (set in loadDocumentInfo)

        // Store the full response for other uses
        this.letterDetails = {
          id: response.id,
          documentName: response.documentName,
          uploadDate: uploadDate,
          assignedDate: assignedDate,
          dueDate: dueDate,
          source: sourceName,
          status: this.getStatusName(response.status) as any,
          assignedTo: this.formatAssignedUsers(response.assignedUsers),
          priority: response.priority || 'N/A', // From API or N/A if not provided
          category: response.category || 'N/A', // From API or N/A if not provided
          fileSize: response.fileSize || 'N/A', // From API or N/A if not provided
          fileType: this.getFileTypeFromFileName(response.uploadedFileName),
          responseDate: '',
          respondedBy: '',
          responseSummary: '',
        };

        this.documentInfoLoading = false;
        this.assignedUsersLoading = false;
      },
      error: (error) => {
        this.documentInfoLoading = false;
        this.assignedUsersLoading = false;
        // Handle error appropriately
      },
    });
  }

  onBack(): void {
    this.router.navigate(['/letters']);
  }

  /**
   * Get source name from source ID
   */
  private getSourceName(sourceId: number): string {
    switch (sourceId) {
      case 1:
        return 'Internal';
      case 2:
        return 'External';
      default:
        return 'Unknown';
    }
  }

  /**
   * Get status name from status code
   */
  private getStatusName(status: number): string {
    switch (status) {
      case 1:
        return 'Pending';
      case 2:
        return 'Responded';
      default:
        return 'Unknown';
    }
  }

  /**
   * Get file type from file name
   */
  private getFileTypeFromFileName(fileName: string): string {
    if (!fileName) return 'Unknown';
    const extension = fileName.split('.').pop()?.toLowerCase();
    switch (extension) {
      case 'pdf':
        return 'PDF';
      case 'doc':
      case 'docx':
        return 'DOC';
      default:
        return extension?.toUpperCase() || 'Unknown';
    }
  }

  /**
   * Format assigned users array into a string
   */
  private formatAssignedUsers(assignedUsers: any[]): string {
    if (!assignedUsers || assignedUsers.length === 0) {
      return 'N/A';
    }
    return assignedUsers.map((user) => user.fullName).join(', ');
  }

  /**
   * Get user initials for avatar display
   */
  getUserInitials(fullName: string): string {
    if (!fullName) return 'NA';
    const names = fullName.split(' ');
    if (names.length >= 2) {
      return (
        names[0].charAt(0) + names[names.length - 1].charAt(0)
      ).toUpperCase();
    }
    return names[0].charAt(0).toUpperCase();
  }

  /**
   * Get status color class for status indicator
   */
  getStatusColor(status: string): string {
    switch (status.toLowerCase()) {
      case 'approved':
        return 'bg-green-500';
      case 'pending':
        return 'bg-orange-500';
      default:
        return 'bg-gray-500';
    }
  }

  /**
   * Get status text color class
   */
  getStatusTextColor(status: string): string {
    switch (status.toLowerCase()) {
      case 'approved':
        return 'text-green-700';
      case 'pending':
        return 'text-orange-700';
      default:
        return 'text-gray-700';
    }
  }

  /**
   * Get display name for status
   */
  getStatusDisplayName(status: string): string {
    switch (status.toLowerCase()) {
      case 'approved':
        return 'Approved';
      case 'pending':
        return 'Pending';
      default:
        return 'Unknown';
    }
  }

  onEdit(): void {
    // Navigate to edit page with the current letter data
    this.router.navigate(['/letters/edit'], {
      queryParams: {
        id: this.documentId,
      },
    });
  }

  onViewDocument(): void {
    if (!this.documentId) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'No document ID available for document download.',
      });
      return;
    }

    // Show modal immediately with loading state
    this.showDocumentViewer = true;
    this.currentDocument = null; // Clear previous document

    // Call the download API to get the document blob
    this.lettersService.downloadLetterDocument(this.documentId).subscribe({
      next: (response: { blob: Blob; filename: string; type: string }) => {
        // Create a blob URL for viewing
        const url = window.URL.createObjectURL(response.blob);

        // Create a document attachment object for the viewer
        this.currentDocument = {
          name: response.filename,
          size: this.formatFileSize(response.blob.size),
          type: response.type, // Use the MIME type from API response
          fileData: url, // Store the blob URL for viewing
          lastModified: Date.now(),
          blobUrl: url, // Store the blob URL separately for cleanup
          originalBlob: response.blob, // Store the original blob for download
        };

        // this.messageService.add({
        //   severity: 'success',
        //   summary: 'Success',
        //   detail: 'Document loaded successfully.'
        // });
      },
      error: (error) => {
        this.showDocumentViewer = false; // Hide modal on error
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load document. Please try again.',
        });
      },
    });
  }

  onViewAuditsLog(): void {
    // Load audit log data from API
    this.loadAuditLog();
    this.selectedRow = this.letterDetails;
    this.auditDialogVisible = true;
  }

  onAuditClose(): void {
    this.auditDialogVisible = false;
  }

  /**
   * Load audit log data from API
   */
  private loadAuditLog(): void {
    if (!this.documentId) {
      return;
    }

    this.lettersService.getAuditLog(this.documentId).subscribe({
      next: (auditLogData) => {
        // Transform the API response to match AuditsData interface
        this.auditData = auditLogData.map((item: any) => ({
          date: formatDateDDMMYYYY(item.createdOn),
          time: this.formatTime(item.createdOn),
          user: item.userName,
          action: item.userAction,
          details: item.userAction, // Using userAction as details
        }));
      },
      error: (error) => {
        this.auditData = []; // Set empty array on error
      },
    });
  }

  /**
   * Format time from ISO string to HH:mm format
   */
  private formatTime(dateString: string): string {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      });
    } catch (error) {
      return '';
    }
  }

  // Manager-specific methods
  onComment(): void {
    // Handle comment action
  }

  onReview(): void {
    // this.showReviewButtons = true; // This line was not in the original file, so it's not added.
  }

  onApprove(): void {
    // Handle approve action
  }

  onReject(): void {
    // Handle reject action
  }

  // Document viewer methods
  onDocumentViewerClose(): void {
    // Clean up blob URL to prevent memory leaks
    if (this.currentDocument?.blobUrl) {
      window.URL.revokeObjectURL(this.currentDocument.blobUrl);
    }
    this.showDocumentViewer = false;
    this.currentDocument = null;
  }

  onDocumentDownload(attachment: DocumentAttachment): void {
    // If we have the original blob from the API, use it directly
    if (attachment.originalBlob) {
      // Create download link
      const url = window.URL.createObjectURL(attachment.originalBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = attachment.name;

      // Trigger download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      // this.messageService.add({
      //   severity: 'success',
      //   summary: 'Success',
      //   detail: 'Document downloaded successfully.'
      // });
    } else if (this.documentId) {
      // If no cached blob but we have document ID, fetch from API
      this.lettersService.downloadLetterDocument(this.documentId).subscribe({
        next: (response: { blob: Blob; filename: string; type: string }) => {
          // Create download link
          const url = window.URL.createObjectURL(response.blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = response.filename;

          // Trigger download
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);

          // this.messageService.add({
          //   severity: 'success',
          //   summary: 'Success',
          //   detail: 'Document downloaded successfully.'
          // });
        },
        error: (error) => {
          // Fallback to local document service
          this.documentService.downloadDocument(attachment);
        },
      });
    } else {
      // Fallback to local document service if no document ID
      this.documentService.downloadDocument(attachment);
    }
  }

  onDocumentOpenInNewTab(attachment: DocumentAttachment): void {
    this.documentService.viewDocument(attachment);
  }

  /**
   * Format file size in human readable format
   * @param bytes File size in bytes
   * @returns Formatted file size string
   */
  private formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  // Archive functionality methods
  onDelete(): void {
    // Show confirmation modal
    this.showDeleteModal = true;
  }

  confirmDeleteLetter(): void {
    // Double check that documentId exists
    if (!this.documentId) {
      this.cancelDeleteLetter();
      return;
    }

    // Prevent multiple archiving attempts
    if (this.isDeleting) {
      return;
    }

    // Set deleting state to show loading feedback
    this.isDeleting = true;

    this.lettersService.deleteLetter(this.documentId).subscribe({
      next: (response) => {
        // Reset delete modal state first
        this.showDeleteModal = false;
        this.isDeleting = false;

        // Show success toast
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: response.message || 'Letter archived successfully.',
          life: 3000,
        });

        // Navigate back to letters list after a delay to show the confirmation message
        setTimeout(() => {
          this.router.navigate(['/letters']);
        }, 2000);
      },
      error: (error) => {
        // Show error toast
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to delete letter. Please try again.',
          life: 5000,
        });

        // Reset delete modal state
        this.showDeleteModal = false;
        this.isDeleting = false;
      },
    });
  }

  cancelDeleteLetter(): void {
    this.showDeleteModal = false;
    this.isDeleting = false;
  }

  /**
   * Get status display text based on approveRejectStatus
   */
  getStatusDisplayText(): string {
    switch (this.approveRejectStatus) {
      case 1:
        return 'Approved';
      case 2:
        return 'Rejected';
      case 3:
      default:
        return 'Pending';
    }
  }

  /**
   * Get status dot color class based on approveRejectStatus
   */
  getStatusDotClass(): string {
    switch (this.approveRejectStatus) {
      case 1:
        return 'bg-green-500'; // Green for approved
      case 2:
        return 'bg-red-500'; // Red for rejected
      case 3:
      default:
        return 'bg-orange-500'; // Orange for pending
    }
  }
}
